import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';

const TdpResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({
    category: '',
    description: '',
    phoneNumber: '',
    correctFirstName: '',
    correctFatherName: '',
    correctGrandFatherName: '',
    correctDepartment: ''
  });
  const [complaintReference, setComplaintReference] = useState('');
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);

  const complaintCategories = [
    { value: 'spelling_error', label: 'Spelling Error' },
    { value: 'no_certificate', label: 'No Certificate Available' },
    { value: 'wrong_department', label: 'Wrong Department' },
    { value: 'unknown_result', label: 'Unknown Result' },
    { value: 'other', label: 'Other' }
  ];

  const departments = [
    'Accounting and Finance',
    'Agriculture',
    'Animal Production',
    'Biology',
    'Chemistry',
    'Citizenship Education',
    'Community Health Service',
    'Computer Maintenance and Networking',
    'Economics',
    'English',
    'Finishing Construction',
    'Geography',
    'History',
    'ICT',
    'Journalism',
    'Marketing and Sales Management',
    'Mathematics',
    'Natural Resource Management',
    'Performing and Visual Arts',
    'Physics',
    'Psychosocial Caregiving',
    'School Leadership',
    'Vocal Performance',
    'Web Design and Development',
    'Woodwork Technology'
  ];

  const handleComplaintSubmit = async () => {
    // Prevent duplicate submissions
    const now = Date.now();
    if (lastSubmissionTime && (now - lastSubmissionTime) < 30000) { // 30 seconds cooldown
      setValidationError('Please wait 30 seconds before submitting another complaint to prevent duplicates.');
      return;
    }

    // Basic validation
    if (!complaintData.category || !complaintData.description || !complaintData.phoneNumber) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    // Conditional validation for spelling error
    if (complaintData.category === 'spelling_error') {
      if (!complaintData.correctFirstName || !complaintData.correctFatherName || !complaintData.correctGrandFatherName) {
        setValidationError('Please fill in all correct name fields for spelling error complaint.');
        return;
      }
    }

    // Conditional validation for wrong department
    if (complaintData.category === 'wrong_department') {
      if (!complaintData.correctDepartment) {
        setValidationError('Please select the correct department.');
        return;
      }
    }

    setValidationError(''); // Clear any previous errors
    setIsSubmittingComplaint(true); // Start loading

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/tdp-complaints`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: result.username,
          full_name: result.full_name || 'N/A',
          category: complaintData.category,
          description: complaintData.description,
          phoneNumber: complaintData.phoneNumber,
          correctFirstName: complaintData.correctFirstName || '',
          correctFatherName: complaintData.correctFatherName || '',
          correctGrandFatherName: complaintData.correctGrandFatherName || '',
          correctDepartment: complaintData.correctDepartment || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Success - close modal and show success message
        setShowComplaintModal(false);
        setShowSuccessModal(true);
        setLastSubmissionTime(Date.now()); // Record submission time
        setComplaintData({ 
          category: '', 
          description: '', 
          phoneNumber: '',
          correctFirstName: '',
          correctFatherName: '',
          correctGrandFatherName: '',
          correctDepartment: ''
        });
        setValidationError(''); // Clear any errors
        // Store the reference number from backend response
        setComplaintReference(data.data?.reference_number || 'N/A');
      } else {
        // Error from server
        setValidationError(data.message || 'Failed to submit complaint. Please try again.');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setValidationError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingComplaint(false); // Stop loading
    }
  };

  const handleCancelComplaint = () => {
    setShowComplaintModal(false);
    setComplaintData({ 
      category: '', 
      description: '', 
      phoneNumber: '',
      correctFirstName: '',
      correctFatherName: '',
      correctGrandFatherName: '',
      correctDepartment: ''
    });
    setValidationError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setComplaintReference('');
  };

  const copyReferenceNumber = async () => {
    try {
      await navigator.clipboard.writeText(complaintReference);
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2000); // Hide confirmation after 2 seconds
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      alert('Failed to copy reference number. Please copy it manually.');
    }
  };

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);
        setError(null);

        const resultData = location.state?.resultData;
        if (!resultData) {
          setError('No result data found');
          return;
        }

        setResult(resultData);
      } catch (err) {
        console.error('Error fetching result:', err);
        setError('Failed to load result data');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [location.state]);

  const downloadCertificate = async () => {
    if (!result) {
      console.error('No result data available');
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch('https://eap.ethernet.edu.et/api/tdp-certificate', {
        method: 'POST',
        headers: {
          'X-API-Key': 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: result.username
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob from the response
      const pdfBlob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TDP_Certificate_${result.username}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading result...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">
            <i className="fa-solid fa-exclamation-triangle"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/tdp-username')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-6xl mb-4">
            <i className="fa-solid fa-file-circle-question"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Result Found</h2>
          <p className="text-gray-600 mb-6">No result data available</p>
          <button
            onClick={() => navigate('/tdp-username')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const passMark = 70; // TDP pass mark is 70%
  
  // Handle incomplete status
  const isIncomplete = result.total_score === 'incomplete' || result.status === 'Incomplete';
  const percentageScore = isIncomplete ? 'Incomplete' : result.total_score;
  const isPassed = isIncomplete ? false : (percentageScore >= passMark);
  const status = isIncomplete ? 'Incomplete' : (result.status || (isPassed ? 'Pass' : 'Fail'));

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/tdp-username')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back</span>
          </button>
        </div>

        {/* Results Header */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-xl font-bold flex items-center mb-4 sm:mb-0" style={{ color: 'rgb(18,82,166)' }}>
              <i className="fa-solid fa-graduation-cap mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
              TDP Result
            </h2>
            <div className="flex gap-3">
              {isPassed && (
                <button
                  onClick={downloadCertificate}
                  disabled={isDownloading}
                  className="flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download Certificate"
                >
                  <i className={`fa-solid ${isDownloading ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`} />
                  {isDownloading ? 'Downloading...' : 'Download Certificate'}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Student Info Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name || 'N/A' },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender || 'N/A' },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name || 'N/A' },
              { icon: 'fa-book', label: 'Subject', value: result.subject || 'N/A' },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: 'rgb(18,82,166)' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Results Table */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Pass Mark</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {percentageScore}%
                      </div>
                      <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            isIncomplete ? 'bg-yellow-500' : (isPassed ? 'bg-green-600' : 'bg-red-600')
                          }`}
                          style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {passMark}%
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Total Score</span>
                    <span className="text-lg font-bold" style={{ color: isIncomplete ? '#d97706' : (isPassed ? '#059669' : '#dc2626') }}>
                      {percentageScore}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        isPassed ? 'bg-green-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-500">Pass Mark</span>
                    <span className="text-lg font-bold text-gray-700">
                      {passMark}%
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Status</span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isIncomplete
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : isPassed 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                      {status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Note */}
        <section className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fa-solid fa-info-circle text-blue-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">Important Note</h3>
              <p className="text-sm text-blue-700 leading-relaxed mb-4">
                This is your official TDP examination result. The complaint period has ended.
              </p>
              <button
                disabled
                className="inline-flex items-center px-4 py-2 bg-gray-400 text-white text-sm font-medium rounded-lg cursor-not-allowed opacity-60"
                title="Complaint period has ended"
              >
                <i className="fa-solid fa-flag mr-2"></i>
                Complaint Period Ended
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-flag mr-3" style={{ color: 'rgb(18,82,166)' }}></i>
                  Submit Complaint
                </h2>
                <button
                  onClick={handleCancelComplaint}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleComplaintSubmit(); }}>
                {/* Category Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complaint Category *
                  </label>
                  <select
                    value={complaintData.category}
                    onChange={(e) => setComplaintData({ ...complaintData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a category</option>
                    {complaintCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Conditional Fields based on Category */}
                {complaintData.category === 'spelling_error' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct First Name *
                      </label>
                      <input
                        type="text"
                        value={complaintData.correctFirstName}
                        onChange={(e) => setComplaintData({ ...complaintData, correctFirstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter correct first name"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct Father Name *
                      </label>
                      <input
                        type="text"
                        value={complaintData.correctFatherName}
                        onChange={(e) => setComplaintData({ ...complaintData, correctFatherName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter correct father name"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct Grand Father Name *
                      </label>
                      <input
                        type="text"
                        value={complaintData.correctGrandFatherName}
                        onChange={(e) => setComplaintData({ ...complaintData, correctGrandFatherName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter correct grand father name"
                        required
                      />
                    </div>
                  </>
                )}

                {complaintData.category === 'wrong_department' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correct Department *
                    </label>
                    <select
                      value={complaintData.correctDepartment}
                      onChange={(e) => setComplaintData({ ...complaintData, correctDepartment: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select correct department</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * ({complaintData.description.length}/255)
                  </label>
                  <textarea
                    value={complaintData.description}
                    onChange={(e) => setComplaintData({ ...complaintData, description: e.target.value })}
                    rows={4}
                    maxLength={255}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      complaintData.description.length > 250 ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={complaintData.category === 'other' ? "Please describe your complaint in detail..." : "Please provide additional details about your complaint..."}
                    required
                  />
                  {complaintData.description.length > 250 && (
                    <p className="text-sm text-red-600 mt-1">
                      Warning: You're approaching the character limit ({complaintData.description.length}/255)
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={complaintData.phoneNumber}
                    onChange={(e) => setComplaintData({ ...complaintData, phoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                {/* Error Message */}
                {validationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{validationError}</p>
                  </div>
                )}

                {/* Duplicate Prevention Info */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <i className="fa-solid fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                    <div>
                      <p className="text-sm text-blue-700 mb-1">
                        If you have multiple complaints, please wait 30 seconds between submissions.
                      </p>
                      <p className="text-xs text-blue-600">
                        You will get a reference number for each complaint individually.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelComplaint}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingComplaint || (lastSubmissionTime && (Date.now() - lastSubmissionTime) < 30000)}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingComplaint ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : (lastSubmissionTime && (Date.now() - lastSubmissionTime) < 30000) ? (
                      <>
                        <i className="fa-solid fa-clock mr-2"></i>
                        Please wait...
                      </>
                    ) : (
                      'Submit Complaint'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            {/* Success Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-check-circle mr-3 text-green-500"></i>
                  Success
                </h2>
                <button
                  onClick={handleCloseSuccessModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Success Modal Body */}
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-check text-2xl text-green-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Complaint Submitted
                </h3>
                <p className="text-gray-600 mb-6">
                  Your complaint has been submitted successfully. We have received your request and will review it shortly.
                </p>
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="fa-solid fa-magnifying-glass text-green-600 mr-2"></i>
                    <p className="text-sm text-green-700">
                      You will get reference numbers for each complaint to see the complaint result.
                    </p>
                  </div>
                </div>
                <div className="p-4 mb-4 rounded-lg border text-center" style={{ 
                  backgroundColor: 'rgba(18, 82, 166, 0.05)', 
                  borderColor: 'rgba(18, 82, 166, 0.2)'
                }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'rgb(18,82,166)' }}>
                    ⚠️ Save This Reference Number:
                  </p>
                  <div className="flex items-center justify-center mb-3">
                    <span className="font-mono font-bold text-base mr-3" style={{ color: 'rgb(18,82,166)' }}>{complaintReference}</span>
                    <button
                      onClick={copyReferenceNumber}
                      className="px-3 py-1 rounded text-sm font-medium hover:bg-blue-100 transition-colors"
                      style={{ color: 'rgb(18,82,166)' }}
                      title="Copy reference number"
                    >
                      <i className="fa-solid fa-copy mr-1"></i>
                      Copy
                    </button>
                    {copyConfirmed && (
                      <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                        <i className="fa-solid fa-check mr-1"></i>
                        Copied!
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Use this reference number to track your complaint status
                  </p>
                </div>
                <button
                  onClick={handleCloseSuccessModal}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdpResultPage;